import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus, Search, X, FileText, Mail, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { useActiveClinic } from '@/lib/activeClinic';
import {
  useInvoicesList,
  formatMoney,
  type InvoiceListFilters,
  type InvoiceListItem,
  type InvoiceStatus,
} from '@/hooks/useInvoices';
import { Button } from '@/components/ui/Button';
import { ErrorAlert } from '@/components/ui/ErrorAlert';

const STATUSES: Array<InvoiceStatus | 'all' | 'overdue'> = [
  'all',
  'draft',
  'issued',
  'overdue',
  'partial',
  'paid',
  'cancelled',
  'refunded',
  'void',
];

type SortColumn = 'invoice_number' | 'patient' | 'status' | 'total' | 'issued_at' | 'due_at';
type SortDirection = 'asc' | 'desc';

const STATUS_SORT_ORDER: Record<InvoiceStatus, number> = {
  draft: 0,
  issued: 1,
  overdue: 2,
  partial: 3,
  paid: 4,
  cancelled: 5,
  refunded: 6,
  void: 7,
  expired_pay_link: 8,
};

function compareNullableDate(a: string | null, b: string | null): number {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return new Date(a).getTime() - new Date(b).getTime();
}

function sortInvoices(
  rows: InvoiceListItem[],
  column: SortColumn,
  direction: SortDirection,
): InvoiceListItem[] {
  const sorted = [...rows];
  const dir = direction === 'asc' ? 1 : -1;

  sorted.sort((a, b) => {
    let cmp = 0;
    switch (column) {
      case 'invoice_number':
        cmp = (a.invoice_number ?? '').localeCompare(b.invoice_number ?? '', 'da', {
          numeric: true,
        });
        break;
      case 'patient': {
        const aName = `${a.patient_last_name ?? ''} ${a.patient_first_name ?? ''}`.trim();
        const bName = `${b.patient_last_name ?? ''} ${b.patient_first_name ?? ''}`.trim();
        cmp = aName.localeCompare(bName, 'da');
        break;
      }
      case 'status':
        cmp = STATUS_SORT_ORDER[a.status] - STATUS_SORT_ORDER[b.status];
        break;
      case 'total':
        cmp = a.total_cents - b.total_cents;
        break;
      case 'issued_at':
        cmp = compareNullableDate(a.issued_at, b.issued_at);
        break;
      case 'due_at':
        cmp = compareNullableDate(a.due_at, b.due_at);
        break;
    }
    return cmp * dir;
  });

  return sorted;
}

export function Invoices({ embedded = false }: { embedded?: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeClinicId } = useActiveClinic();

  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | 'all' | 'overdue'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState<SortColumn>('issued_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const filters = useMemo<InvoiceListFilters>(
    () => ({
      status: statusFilter === 'overdue' ? 'issued' : statusFilter,
      search: searchTerm.trim() || undefined,
    }),
    [statusFilter, searchTerm],
  );

  const { data, isLoading, error } = useInvoicesList(activeClinicId, filters);
  const now = new Date();
  const invoices = useMemo(() => {
    const all = data ?? [];
    if (statusFilter === 'overdue') {
      return all.filter(
        (inv) => inv.status === 'issued' && inv.due_at != null && new Date(inv.due_at) < now,
      );
    }
    return all;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, statusFilter]);

  const sortedInvoices = useMemo(
    () => sortInvoices(invoices, sortColumn, sortDirection),
    [invoices, sortColumn, sortDirection],
  );

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortColumn(column);
    setSortDirection(column === 'invoice_number' || column === 'patient' ? 'asc' : 'desc');
  };

  const content = (
    <>
          <div className={`${embedded ? 'mt-6' : ''} md:flex md:items-center md:justify-between`}>
            {!embedded && (
              <div className="min-w-0 flex-1">
                <h2 className="text-2xl font-bold leading-7 text-gray-900 dark:text-white sm:text-3xl sm:tracking-tight">
                  {t('invoices.title')}
                </h2>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {t('invoices.subtitle')}
                </p>
              </div>
            )}
            <div className={`flex ${embedded ? 'justify-end' : 'mt-4 md:ml-4 md:mt-0'}`}>
              <Button onClick={() => navigate('/invoices/new')} disabled={!activeClinicId}>
                <Plus className="mr-1 h-4 w-4" />
                {t('invoices.new_invoice')}
              </Button>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <div className="relative max-w-md flex-1">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('invoices.search_placeholder')}
                className="block w-full rounded-md border border-gray-300 bg-white py-2 pl-10 pr-3 text-sm leading-5 placeholder-gray-500 focus:border-indigo-500 focus:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-500"
                  aria-label={t('invoices.clear_search')}
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>

            <div className="inline-flex rounded-md border border-gray-300 bg-white shadow-sm dark:border-gray-600 dark:bg-gray-800">
              {STATUSES.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatusFilter(s)}
                  className={`px-3 py-1.5 text-sm first:rounded-l-md last:rounded-r-md ${
                    statusFilter === s
                      ? s === 'overdue'
                        ? 'bg-orange-500 text-white'
                        : 'bg-indigo-600 text-white'
                      : 'text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700'
                  }`}
                >
                  {s === 'overdue'
                    ? t('invoices.status_overdue')
                    : t(`invoices.status_${s === 'all' ? 'all' : s}`)}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div className="mt-6">
              <ErrorAlert message={t('invoices.load_error')} />
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : sortedInvoices.length === 0 ? (
            <EmptyState hasSearch={Boolean(searchTerm)} />
          ) : (
            <div className="mt-6 overflow-hidden sm:rounded-md">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <SortableTh
                        active={sortColumn === 'invoice_number'}
                        direction={sortDirection}
                        onSort={() => handleSort('invoice_number')}
                      >
                        {t('invoices.col_number')}
                      </SortableTh>
                      <SortableTh
                        active={sortColumn === 'patient'}
                        direction={sortDirection}
                        onSort={() => handleSort('patient')}
                      >
                        {t('invoices.col_patient')}
                      </SortableTh>
                      <SortableTh
                        active={sortColumn === 'status'}
                        direction={sortDirection}
                        onSort={() => handleSort('status')}
                      >
                        {t('invoices.col_status')}
                      </SortableTh>
                      <SortableTh
                        active={sortColumn === 'total'}
                        direction={sortDirection}
                        onSort={() => handleSort('total')}
                      >
                        {t('invoices.col_total')}
                      </SortableTh>
                      <SortableTh
                        active={sortColumn === 'issued_at'}
                        direction={sortDirection}
                        onSort={() => handleSort('issued_at')}
                      >
                        {t('invoices.col_issued_at')}
                      </SortableTh>
                      <SortableTh
                        active={sortColumn === 'due_at'}
                        direction={sortDirection}
                        onSort={() => handleSort('due_at')}
                      >
                        {t('invoices.col_due_at')}
                      </SortableTh>
                      <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                        <span className="sr-only">{t('invoices.col_actions')}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-card-dark">
                    {sortedInvoices.map((inv) => (
                      <tr
                        key={inv.id}
                        onClick={() => navigate(`/invoices/${inv.id}`)}
                        className="cursor-pointer transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-800"
                      >
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 font-mono text-sm text-gray-900 dark:text-white sm:pl-6">
                          {inv.invoice_number ?? t('invoices.no_number')}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          <div className="font-medium text-gray-900 dark:text-white">
                            {inv.patient_first_name} {inv.patient_last_name}
                          </div>
                          {inv.patient_pat_number && (
                            <div className="font-mono text-xs text-gray-400">
                              {inv.patient_pat_number}
                            </div>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm">
                          <InvoiceStatusBadges status={inv.status} lastSentAt={inv.last_sent_at} lastReminderSentAt={inv.last_reminder_sent_at} />
                          {inv.status === 'partial' && (
                            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                              {formatMoney(inv.total_paid_cents, inv.currency)} /{' '}
                              {formatMoney(inv.total_cents, inv.currency)}
                            </div>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900 dark:text-white">
                          {formatMoney(inv.total_cents, inv.currency)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                          {inv.issued_at
                            ? new Date(inv.issued_at).toLocaleDateString('da-DK')
                            : '—'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                          {inv.due_at
                            ? new Date(inv.due_at).toLocaleDateString('da-DK')
                            : t('invoices.no_due_date')}
                          {inv.status === 'issued' &&
                            inv.due_at != null &&
                            new Date(inv.due_at) < new Date() && (
                              <span className="ml-2 inline-flex items-center rounded-full bg-orange-100 px-1.5 py-0.5 text-xs font-medium text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                                {t('invoices.status_overdue')}
                              </span>
                            )}
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/invoices/${inv.id}`);
                            }}
                            className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                          >
                            {t('invoices.see_details')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
    </>
  );

  if (embedded) return content;

  return (
    <div className="min-h-screen bg-page-light dark:bg-page-dark">
      <main className="py-6">
        <div className="max-w-none px-6">{content}</div>
      </main>
    </div>
  );
}

function SortableTh({
  children,
  active,
  direction,
  onSort,
}: {
  children: React.ReactNode;
  active: boolean;
  direction: SortDirection;
  onSort: () => void;
}) {
  const { t } = useTranslation();
  const SortIcon = active ? (direction === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;

  return (
    <th
      scope="col"
      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 first:pl-4 dark:text-white first:sm:pl-6"
    >
      <button
        type="button"
        onClick={onSort}
        className="group inline-flex items-center gap-1 hover:text-indigo-600 dark:hover:text-indigo-400"
        aria-label={
          active
            ? direction === 'asc'
              ? t('invoices.sort_asc')
              : t('invoices.sort_desc')
            : undefined
        }
      >
        {children}
        <SortIcon
          className={`h-3.5 w-3.5 shrink-0 ${
            active ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400 group-hover:text-indigo-500'
          }`}
        />
      </button>
    </th>
  );
}

export function StatusBadge({ status }: { status: InvoiceListItem['status'] }) {
  const { t } = useTranslation();
  const cls: Record<InvoiceStatus, string> = {
    draft: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
    issued: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    overdue: 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
    partial: 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
    paid: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    cancelled: 'bg-gray-50 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
    refunded: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    void: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    expired_pay_link: 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls[status]}`}
    >
      {t(`invoices.status_${status}`)}
    </span>
  );
}

export function InvoiceStatusBadges({
  status,
  lastSentAt,
  lastReminderSentAt,
}: {
  status: InvoiceListItem['status'];
  lastSentAt?: string | null;
  lastReminderSentAt?: string | null;
}) {
  const { t } = useTranslation();
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <StatusBadge status={status} />
      {lastSentAt && status !== 'draft' && (
        <span
          className="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300"
          title={
            lastSentAt
              ? t('invoices.email_sent_at', {
                  date: new Date(lastSentAt).toLocaleString('da-DK'),
                })
              : undefined
          }
        >
          <Mail className="mr-1 h-3 w-3" />
          {t('invoices.status_email_sent')}
        </span>
      )}
      {lastReminderSentAt && status === 'issued' && (
        <span
          className="inline-flex items-center rounded-full bg-orange-50 px-2 py-0.5 text-xs font-medium text-orange-700 dark:bg-orange-900/30 dark:text-orange-300"
          title={
            lastReminderSentAt
              ? t('invoices.reminder_sent_at', {
                  date: new Date(lastReminderSentAt).toLocaleString('da-DK'),
                })
              : undefined
          }
        >
          <Mail className="mr-1 h-3 w-3" />
          {t('invoices.status_reminder_sent')}
        </span>
      )}
    </span>
  );
}

function EmptyState({ hasSearch }: { hasSearch: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="mt-12 text-center">
      <FileText className="mx-auto h-10 w-10 text-gray-400" />
      <h3 className="mt-4 text-base font-medium text-gray-900 dark:text-white">
        {hasSearch ? t('invoices.empty_search_title') : t('invoices.empty_title')}
      </h3>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        {hasSearch ? t('invoices.empty_search_subtitle') : t('invoices.empty_subtitle')}
      </p>
    </div>
  );
}
